import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Button, { ButtonLink } from "@/components/ui/Button";
import { Field, Input, Textarea } from "@/components/ui/Field";
import { useForm, usePage } from "@inertiajs/react";
import type { SharedProps } from "@/types";
import { ageFrom, isMinorOn } from "@/utils/age";

// An application to be the point of contact for a Boba Bash in a city. Venue,
// date and team all come later, once someone's been picked - so this only asks
// who they are and where.
export default function OrganizeNew({
  organizer_details,
}: {
  organizer_details: {
    birthday: string | null;
    parent_name: string | null;
    parent_email: string | null;
    minor: boolean;
    on_file: boolean;
  };
}) {
  const { data, setData, post, processing, errors } = useForm({
    city: "",
    region: "",
    country: "",
    expected_attendees: "",
    organizer_slack_id: "",
    birthday: organizer_details.birthday ?? "",
    parent_name: organizer_details.parent_name ?? "",
    parent_email: organizer_details.parent_email ?? "",
    pitch: "",
    experience: "",
  });

  // Hack Club Auth hands us a birthdate at sign-in, so almost nobody has to
  // type one here. When it's missing the field comes back, because without a
  // date of birth HCB can't open the account and the application can't submit.
  const knownBirthday = organizer_details.on_file;
  // Recomputed from the field rather than read off the prop, so the guardian
  // block appears as soon as they type a date rather than after a save.
  const isMinor = isMinorOn(data.birthday);
  // Applications reopened for US cities only while the rest of the program
  // moves to October. Enforced on the record too - this only makes the refusal
  // arrive before eight fields of typing rather than after.
  const usOnly =
    usePage<SharedProps>().props.program.organizer_applications_us_only;

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          title="Apply to organize"
          subtitle="You're applying to be the point of contact for a Boba Bash in your city. You don't need a venue or a date yet - we'll figure that out together."
          actions={
            <ButtonLink href="/guides/organizer-handbook" variant="secondary">
              Read the handbook
            </ButtonLink>
          }
        />

        {/* Said before the form rather than as an error after it. Somebody
            outside the US filling in eight fields and then being refused is a
            worse way to find out. */}
        {usOnly && (
          <Panel
            title="where-were-looking.txt"
            tone="alt"
            className="max-w-2xl"
          >
            <p className="text-body text-ink">
              We're only taking <strong>new US applications</strong> right now.
              The rest of the program has moved to October and those cities are
              already staffed - the gap we need to fill is here.
            </p>
          </Panel>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            post("/organize");
          }}
        >
          <Panel title="application.form" className="max-w-2xl">
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="City" error={errors.city}>
                  <Input
                    value={data.city}
                    onChange={(e) => setData("city", e.target.value)}
                    placeholder="San Jose"
                    required
                  />
                </Field>

                <Field label="State / region" error={errors.region}>
                  <Input
                    value={data.region}
                    onChange={(e) => setData("region", e.target.value)}
                    placeholder="California"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field
                  label="Country"
                  hint={usOnly ? "US cities only right now." : undefined}
                  error={errors.country}
                >
                  <Input
                    value={data.country}
                    onChange={(e) => setData("country", e.target.value)}
                    placeholder="USA"
                    required
                  />
                </Field>

                <Field
                  label="Roughly how many people?"
                  hint="A guess is completely fine."
                  error={errors.expected_attendees}
                >
                  <Input
                    type="number"
                    min={1}
                    value={data.expected_attendees}
                    onChange={(e) =>
                      setData("expected_attendees", e.target.value)
                    }
                    placeholder="25"
                    required
                  />
                </Field>
              </div>

              <Field
                label="Slack member ID"
                hint="How we reach you while you're planning. In Slack, open your profile, click the ⋮ and choose “Copy member ID”."
                error={errors.organizer_slack_id}
              >
                <Input
                  value={data.organizer_slack_id}
                  onChange={(e) =>
                    setData("organizer_slack_id", e.target.value)
                  }
                  placeholder="U01ABC2DEFG"
                  required
                />
              </Field>

              {!knownBirthday && (
                <Field
                  label="Your date of birth"
                  hint="Boba Bash is run by and for teenagers, and HCB needs it to set up your event's money."
                  error={errors.birthday}
                >
                  <Input
                    type="date"
                    value={data.birthday}
                    onChange={(e) => setData("birthday", e.target.value)}
                    required
                  />
                </Field>
              )}

              {isMinor && (
                <div className="border-line bg-surface-plain flex flex-col gap-5 border-2 p-4">
                  <p className="text-caption text-ink-muted">
                    {knownBirthday
                      ? `Your Hack Club account puts you at ${ageFrom(data.birthday)}, so a parent or guardian has to co-sign the HCB agreement.`
                      : "You're under 18, so a parent or guardian has to co-sign the HCB agreement."}{" "}
                    We'll email them the form - they don't need an account.
                  </p>

                  <Field
                    label="Parent or guardian's name"
                    error={errors.parent_name}
                  >
                    <Input
                      value={data.parent_name}
                      onChange={(e) => setData("parent_name", e.target.value)}
                      required
                    />
                  </Field>

                  <Field
                    label="Parent or guardian's email"
                    error={errors.parent_email}
                  >
                    <Input
                      type="email"
                      value={data.parent_email}
                      onChange={(e) => setData("parent_email", e.target.value)}
                      required
                    />
                  </Field>
                </div>
              )}

              <Field
                label="Why do you want to run one?"
                hint="A few sentences about you and what you're picturing. No wrong answers."
                error={errors.pitch}
              >
                <Textarea
                  rows={4}
                  value={data.pitch}
                  onChange={(e) => setData("pitch", e.target.value)}
                  required
                />
              </Field>

              <Field
                label="Have you run something like this before?"
                hint="Optional. It's completely fine if this is your first one."
                error={errors.experience}
              >
                <Textarea
                  rows={3}
                  value={data.experience}
                  onChange={(e) => setData("experience", e.target.value)}
                />
              </Field>

              <Button
                type="submit"
                disabled={processing}
                className="self-start"
              >
                {processing ? "Submitting…" : "Submit application"}
              </Button>
            </div>
          </Panel>
        </form>
      </div>
    </Layout>
  );
}
